import { toast } from "@/hooks/use-toast";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function shortenAddress(address: string, chars = 4): string {
  return `${address.substring(0, chars)}...${address.substring(address.length - chars)}`;
}

export function copyText({
  value,
  titleToast,
  descriptionToast,
} : {
  value: string;
  titleToast: string;
  descriptionToast: string;
}) {
  if (value.length > 0) {
    navigator.clipboard.writeText(value);
    toast({
      title: titleToast,
      description: descriptionToast,
    });
  }
};

export function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export function formatDateRange(startDate: Date | string | null, endDate: Date | string | null): string | null {
  if (!startDate || !endDate) {
    return null;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  const startDay = start.getDate();
  const endDay = end.getDate();
  const startMonth = start.toLocaleDateString('en-US', { month: 'long' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'long' });
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  // Same year, same month: "10-15 March 2026"
  if (start.getMonth() === end.getMonth() && startYear === endYear) {
    return `${startDay}-${endDay} ${startMonth} ${endYear}`;
  }

  // Different years: "10 March 2025 - 20 March 2026"
  if (startYear !== endYear) {
    return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
  }

  // Same year, different months: "10 March-20 May 2026"
  return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}`;
}

export function formatTimelineDateRange(startDate: Date | string | null, endDate: Date | string | null): string | null {
  if (!startDate || !endDate) {
    return null;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  const startDay = start.getDate();
  const endDay = end.getDate();
  const startMonth = start.toLocaleDateString('en-US', { month: 'long' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'long' });

  // Same month: "March 10 - 14"
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${startMonth} ${startDay} - ${endDay}`;
  }

  // Different months: "March 28 - April 5"
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
}

export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
}

export function formatLockToken(balance: number | string): string {
  const num = parseFloat(balance.toString());
  return num >= 1000 ? `${(num / 1000).toFixed(0)}k` : `${num.toFixed(4)}`;
}

export function formatBalance(balance: number | string): string {
  const num = parseFloat(balance.toString());

  if (num === 0) {
    return '0';
  }

  // Convert to string with high precision to handle small decimals
  let str = num.toString();

  // Handle scientific notation (e.g., 1e-7)
  if (str.includes('e')) {
    str = num.toFixed(20);
  }

  // Split into integer and decimal parts
  const [, decimal] = str.split('.');

  // If no decimal part, return with 4 decimals
  if (!decimal) {
    return num.toFixed(4);
  }

  // Find first non-zero digit position (1-indexed)
  const firstNonZeroPos = decimal.split('').findIndex(d => d !== '0') + 1;

  // If all decimals are zero, return with 4 decimals
  if (firstNonZeroPos === 0) {
    return num.toFixed(4);
  }

  // Keep one digit after the first non-zero digit
  const keepDecimals = firstNonZeroPos + 1;

  // Truncate the decimal string (no rounding)
  const [intPart] = str.split('.');
  const truncatedDecimal = decimal.substring(0, keepDecimals);

  return `${intPart}.${truncatedDecimal}`;
}

export function formatThousands(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return num.toLocaleString();
}

export function formatNumber(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (num === 0) {
    return '0';
  }

  // Convert to string with high precision to handle small decimals
  let str = num.toString();

  // Handle scientific notation (e.g., 1e-7)
  if (str.includes('e')) {
    str = num.toFixed(20);
  }

  const [intPart, decimal] = str.split('.');

  // If no decimal part, format with thousand separators
  if (!decimal) {
    return parseInt(intPart).toLocaleString();
  }

  // Find first non-zero digit position (1-indexed)
  const firstNonZeroPos = decimal.split('').findIndex(d => d !== '0') + 1;

  // If all decimals are zero, format integer with thousand separators
  if (firstNonZeroPos === 0) {
    return parseInt(intPart).toLocaleString();
  }

  // For values >= 1, keep up to 4 significant decimals and remove trailing zeros
  if (Math.abs(num) >= 1) {
    const maxDecimals = 4;
    const truncatedDecimal = decimal.substring(0, maxDecimals).replace(/0+$/, '');
    const formattedInt = parseInt(intPart).toLocaleString();
    return truncatedDecimal ? `${formattedInt}.${truncatedDecimal}` : formattedInt;
  }

  // For values < 1, keep one digit after the first non-zero digit
  const keepDecimals = firstNonZeroPos + 1;
  const truncatedDecimal = decimal.substring(0, keepDecimals);
  return `${intPart}.${truncatedDecimal}`;
}

export function getXDaoHomePath(): string {
    return '/xdao'
}
